import { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Alert
} from '@mui/material';
import { Modal } from '@components/Modal';
import {
  TestPointPicker,
  type SelectedTestPoint,
  type CatalogSelection
} from './TestPointPicker';
import type { Script } from '../types';
import type {
  TestPointRef,
  ItemScriptData
} from '../types/steps';

type Props = {
  open: boolean;
  onClose: () => void;
  onCreate: (entries: CreateItemEntry[]) => void;
  isPending: boolean;
  siblings: Script[];
  masterScript: Script;
  disabledTestPointIds: string[];
  duplicateSource?: ItemScriptData | null;
};

export type CreateItemEntry = {
  name: string;
  scriptData: string;
};

const AddTestPointsDialog = ({
  open,
  onClose,
  onCreate,
  isPending,
  masterScript: _masterScript,
  disabledTestPointIds,
  duplicateSource
}: Props) => {
  const [selectedEntries, setSelectedEntries] = useState<SelectedTestPoint[]>(
    []
  );
  const [catalogSelection, setCatalogSelection] =
    useState<CatalogSelection | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedEntries([]);
      setCatalogSelection(null);
    }
  }, [open]);

  const handleSelectionChange = useCallback((entries: SelectedTestPoint[]) => {
    setSelectedEntries(entries);
  }, []);

  const handleSubmit = () => {
    if (selectedEntries.length === 0) return;

    const entries: CreateItemEntry[] = selectedEntries.map(entry => {
      const testPointRef: TestPointRef = {
        functionId: entry.func.id,
        nominal: entry.testPoint.nominal,
        unit: entry.func.unit,
        mode: entry.func.mode,
        label: entry.testPoint.label,
        ...(entry.func.defaultFrequency
          ? { frequency: entry.func.defaultFrequency }
          : {})
      };

      const itemData: ItemScriptData = {
        testPointRef,
        beforeSteps: duplicateSource
          ? structuredClone(duplicateSource.beforeSteps ?? [])
          : [],
        calibrationSteps: duplicateSource
          ? structuredClone(duplicateSource.calibrationSteps ?? [])
          : [],
        afterSteps: duplicateSource
          ? structuredClone(duplicateSource.afterSteps ?? [])
          : []
      };

      const name = `${entry.func.id.toUpperCase()}-${entry.testPoint.label}`;
      return { name, scriptData: JSON.stringify(itemData, null, 2) };
    });

    onCreate(entries);
  };

  const handleClose = () => {
    setSelectedEntries([]);
    onClose();
  };

  const isDuplicateMode = !!duplicateSource;

  const canSubmit = selectedEntries.length > 0;

  const sourceLabel =
    isDuplicateMode && duplicateSource?.testPointRef
      ? `${duplicateSource.testPointRef.functionId.toUpperCase()} — ${duplicateSource.testPointRef.nominal} ${duplicateSource.testPointRef.unit}`
      : '';

  const stepCount = isDuplicateMode
    ? (duplicateSource?.beforeSteps?.length ?? 0) +
      (duplicateSource?.calibrationSteps?.length ?? 0) +
      (duplicateSource?.afterSteps?.length ?? 0)
    : 0;

  const buttonLabel = isDuplicateMode
    ? `Duplicate to ${selectedEntries.length} Test Point${selectedEntries.length !== 1 ? 's' : ''}`
    : `Add ${selectedEntries.length} Test Point${selectedEntries.length !== 1 ? 's' : ''}`;

  return (
    <Modal open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Modal.Header
        title={isDuplicateMode ? 'Duplicate Test Point' : 'Add Test Points'}
        onClose={handleClose}
      />
      <Modal.Content>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {isDuplicateMode && (
            <Alert severity="info">
              Steps from <strong>{sourceLabel}</strong> ({stepCount} step
              {stepCount !== 1 ? 's' : ''}) will be copied to each selected test
              point.
            </Alert>
          )}

          <TestPointPicker
            onSelectionChange={handleSelectionChange}
            onCatalogChange={setCatalogSelection}
            disabledTestPointIds={disabledTestPointIds}
            hideGenerateButton
          />

          {catalogSelection?.firmwareCommand && (
            <Alert severity="info" sx={{ mt: 1 }} variant="outlined">
              Firmware command{' '}
              <strong>{catalogSelection.firmwareCommand}</strong> from catalog{' '}
              <strong>{catalogSelection.catalogName}</strong>
              {catalogSelection.modelVariant
                ? ` (${catalogSelection.modelVariant})`
                : ''}{' '}
              is selected — bake it into the master script&apos;s pickupSteps if
              you have not already.
            </Alert>
          )}
        </Box>
      </Modal.Content>
      <Modal.Footer
        primaryButton={{
          text: buttonLabel,
          onClick: handleSubmit,
          disabled: !canSubmit || isPending
        }}
        secondaryButton={{
          text: 'Cancel',
          onClick: handleClose,
          variant: 'text' as const
        }}
      />
    </Modal>
  );
};

export default AddTestPointsDialog;
